import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Moment } from 'moment';
import { DatePicker, Dialog, Spinner } from '@rio-cloud/rio-uikit';
import { Consultant } from '../../../../models/consultant';
import { UserEvent, getUserEventMessageId } from '../../../../models/userEvent';
import { fetchUserEvents } from '../../../../api/userEvents';

const DEFAULT_PERIOD_IN_DAYS = 30;
const PAGE_SIZE = 50;

interface ConsultantActivityDialogProps {
  consultant: Consultant;
  onClose: () => void;
}

const startOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

const endOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

const getDefaultStartDate = (): Date => {
  const result = startOfDay(new Date());
  result.setDate(result.getDate() - DEFAULT_PERIOD_IN_DAYS);
  return result;
};

const ConsultantActivityDialog = ({ consultant, onClose }: ConsultantActivityDialogProps) => {
  const intl = useIntl();
  const [events, setEvents] = useState<UserEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [startDate, setStartDate] = useState<Date>(getDefaultStartDate);
  const [endDate, setEndDate] = useState<Date>(() => endOfDay(new Date()));
  const requestIdRef = useRef(0);

  const consultantId = consultant.id;

  const loadEvents = async (userId: string, page: number, append: boolean) => {
    const requestId = ++requestIdRef.current;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(false);

    try {
      const data = await fetchUserEvents(userId, { page, limit: PAGE_SIZE, startDate, endDate });
      if (requestId !== requestIdRef.current) return;

      setEvents(prev => (append ? [...prev, ...data] : data));
      setHasMoreData(data.length === PAGE_SIZE);
      setCurrentPage(page);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(true);
      console.error('Error loading user events:', err);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    setEvents([]);
    setHasMoreData(false);
    if (!consultantId) return;

    loadEvents(consultantId, 0, false);
  }, [consultantId, startDate, endDate]);

  const handleStartDateChange = (value: Moment | string) => {
    if (typeof value !== 'string' && value.isValid()) setStartDate(startOfDay(value.toDate()));
  };

  const handleEndDateChange = (value: Moment | string) => {
    if (typeof value !== 'string' && value.isValid()) setEndDate(endOfDay(value.toDate()));
  };

  const formatDateTime = (date?: Date): string =>
    date ? intl.formatDate(date, { dateStyle: 'short', timeStyle: 'short' }) : '-';

  const formatEventType = (eventType: string): string => {
    const messageId = getUserEventMessageId(eventType);
    return messageId ? intl.formatMessage({ id: messageId }) : eventType;
  };

  return (
    <Dialog
      show={true}
      onClose={onClose}
      showCloseButton={true}
      title={intl.formatMessage(
        { id: 'intl-msg:backoffice.activity.title' },
        { name: `${consultant.firstName ?? ''} ${consultant.lastName ?? ''}`.trim() }
      )}
      bsSize={Dialog.SIZE_MD}
      body={(
        <div>
          <div className="display-flex gap-10 margin-bottom-15">
            <div className="width-200">
              <label htmlFor="activityStartDate">{intl.formatMessage({ id: 'intl-msg:backoffice.activity.filter.startDate' })}</label>
              <DatePicker
                id="activityStartDate"
                value={startDate}
                dateFormat="DD/MM/YYYY"
                timeFormat={false}
                locale={intl.locale}
                closeOnSelect
                isValidDate={(current: Moment) => !current.isAfter(endDate)}
                onChange={handleStartDateChange}
              />
            </div>
            <div className="width-200">
              <label htmlFor="activityEndDate">{intl.formatMessage({ id: 'intl-msg:backoffice.activity.filter.endDate' })}</label>
              <DatePicker
                id="activityEndDate"
                value={endDate}
                dateFormat="DD/MM/YYYY"
                timeFormat={false}
                locale={intl.locale}
                closeOnSelect
                isValidDate={(current: Moment) => !current.isBefore(startDate) && !current.isAfter(endOfDay(new Date()))}
                onChange={handleEndDateChange}
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Spinner />
            </div>
          ) : error ? (
            <div className="text-red-600 text-center py-12">
              <p className="font-medium">{intl.formatMessage({ id: 'intl-msg:backoffice.activity.error' })}</p>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500">
                <p className="font-medium mb-2">{intl.formatMessage({ id: 'intl-msg:backoffice.activity.empty' })}</p>
              </div>
            </div>
          ) : (
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left font-medium text-gray-500">{intl.formatMessage({ id: 'intl-msg:backoffice.activity.column.dateTime' })}</th>
                  <th className="px-6 py-3 text-left font-medium text-gray-500">{intl.formatMessage({ id: 'intl-msg:backoffice.activity.column.activity' })}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {events.map((event, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900">{formatDateTime(event.createdAt)}</td>
                    <td className="px-6 py-4 text-gray-900">{formatEventType(event.eventType)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {hasMoreData && consultantId && (
            <div className="display-flex justify-content-center margin-top-15">
              <button
                className="btn btn-link"
                type="button"
                onClick={() => loadEvents(consultantId, currentPage + 1, true)}
                disabled={loadingMore}
              >
                <span className={`rioglyph ${loadingMore ? 'rioglyph-spinner spinning' : 'rioglyph-arrow-down'}`}></span>
                {intl.formatMessage({ id: loadingMore ? 'intl-msg:backoffice.activity.loading' : 'intl-msg:backoffice.activity.loadMore' })}
              </button>
            </div>
          )}
        </div>
      )}
    />
  );
};

export default ConsultantActivityDialog;
